import React from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, CalendarIcon, ArrowRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export interface OpportunityItem {
  id: string;
  title: string;
  poster: string;
  posterImage?: string;
  deadline: string;
  type: string;
  description: string;
  link?: string;
}
interface OpportunityPreviewCardProps {
  opportunity: OpportunityItem;
  baseLink: string;
}
export const OpportunityPreviewCard: React.FC<OpportunityPreviewCardProps> = ({
  opportunity,
  baseLink
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Calculate days remaining
  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const daysRemaining = getDaysRemaining(opportunity.deadline);
  const isUrgent = daysRemaining <= 7;
  const isClosingSoon = daysRemaining <= 14;
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className={`rounded-xl border p-5 transition-all hover:shadow-md ${dk.card} ${dk.border}`} data-id="element-1232" data-mp="1jgvvhi0asqcq">
      <div className="flex justify-between items-start gap-4 mb-3" data-id="element-1233" data-mp="vr1f4qgrsjnq">
        <div data-id="element-1234" data-mp="qrotg91mximq">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`} data-id="element-1235" data-mp="qs81my1hgsnk">
            {opportunity.type}
          </span>
          <h3 className={`font-bold text-lg leading-tight mb-1 ${dk.textPrimary}`} data-id="element-1236" data-mp="zpqv8qt70fb2">
            {opportunity.title}
          </h3>
        </div>
        <div className={`flex-shrink-0 flex items-center px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium ${isUrgent ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : isClosingSoon ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`} data-id="element-1237" data-mp="7zw3yolryfjr">
          <ClockIcon className="h-3 w-3 mr-1" data-id="element-1238" data-mp="2luuzygo8nmzt" />
          {daysRemaining > 0 ? `${daysRemaining} days left` : 'Closed'}
        </div>
      </div>

      <p className={`text-sm mb-4 line-clamp-2 ${dk.textSecondary}`} data-id="element-1239" data-mp="1o8e65leprrit">
        {opportunity.description}
      </p>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700" data-id="element-1240" data-mp="1t7qu69hbn4mz">
        <div className="flex items-center" data-id="element-1241" data-mp="1y73lp7j50zvw">
          {opportunity.posterImage ? <img src={opportunity.posterImage} alt={opportunity.poster} className="h-6 w-6 rounded-full object-cover mr-2" data-id="element-1242" data-mp="231r4qqvdh233" /> : <div className={`h-6 w-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`} data-id="element-1243" data-mp="3el3n6mxmsxl9">
              {opportunity.poster.charAt(0)}
            </div>}
          <span className={`text-xs ${dk.textMuted}`} data-id="element-1244" data-mp="t49j6f4ca8hz">
            Posted by{' '}
            <span className={dk.textPrimary} data-id="element-1245" data-mp="o5y8k267ztyp">{opportunity.poster}</span>
          </span>
        </div>

        <Link to={baseLink} // In a real app, this might link to a specific ID
      className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-1246" data-mp="j8fy27ck16ol">
          View Details <ArrowRightIcon className="h-3 w-3 ml-1" data-id="element-1247" data-mp="a818uwwgz7ft" />
        </Link>
      </div>
    </div>;
};